import { CartRecommendationActions } from "../../actions/CartRecommendationActions";
import { LazyStore } from "../../utils/Store";
import { trackUiError } from "../../utils/TrackingUtil";
import { getDidYouForgetRecommendation, downvoteBundle } from "../../server/CartmancerRequests";
import { CustomerCartStoreImpl } from "./CustomerCartStore";
import { CartItemStoreImpl } from "../common-cart/CartItemStore";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import type { ArticleData } from "../../model/ArticleTypes";
import type { SessionContext } from "../../model/CustomerCartTypes";
import { getNumberOfItems } from "../../utils/CartSizeUtil";
import { GdprStorage } from "../../uidispatcherapi/GdprStorage";
import { normalizeInputQuantity } from "../../utils/QuantityConverter";
export type DidYouForgetPrediction = {
	predictionId: string;
	items: string[];
};
type CartRecommendationStoreInternalState = {
	cartId: string | null | undefined;
	prediction: DidYouForgetPrediction | null | undefined;
};
export type PredictionWithArticleData = {
	predictionId: string;
	items: Array<[string, ArticleData | null | undefined]>;
};
export type CartRecommendationStoreState = {
	prediction: PredictionWithArticleData | null | undefined;
	enabled: boolean;
	numberOfItemsInCart: number | null | undefined;
};
// Change this, when you want to force users to see the cart recommendations again,
// even though they opt-outed before.
const CART_RECOMMENDATION_OPT_OUT_VALUE = "v1";
export class CartRecommendationStoreImpl extends LazyStore {
	_cartStore: CustomerCartStoreImpl;
	_cartItemStore: CartItemStoreImpl<SessionContext>;
	_state: CartRecommendationStoreInternalState;
	_hiddenBundles: Set<string>;

	constructor(cartStore: CustomerCartStoreImpl, cartItemStore: CartItemStoreImpl<SessionContext>) {
		super();
		this._cartStore = cartStore;
		this._cartItemStore = cartItemStore;
		this._hiddenBundles = new Set();
		this._state = {
			cartId: null,
			prediction: null
		};
	}

	onArm = () => {
		this.listenTo(this._cartStore, this.onCartStoreUpdate);
		this.listenTo(this._cartItemStore, this.trigger);
		this.listenTo(CartRecommendationActions.addItem, this.onAddItem);
		this.listenTo(CartRecommendationActions.removeItem, (bundleId) => {
			if (!this._state.cartId) {
				return;
			}

			this.hideRecommendation(bundleId).catch((error) => {
				console.error("hideRecommendation in CartRecommendationStore failed with ", error);
			});
		});
		this.listenTo(CartRecommendationActions.closeRecommendation, () => {
			GdprStorage.localStorage.put(
				"cart-recommendation-opt-out",
				CART_RECOMMENDATION_OPT_OUT_VALUE,
				"functional"
			);
			this.trigger();
		});
		this.onCartStoreUpdate();
	};

	onAddItem = (bundleId, articleData) => {
		this._cartStore
			.addItem({
				bundleId,
				quantity: normalizeInputQuantity(
					1,
					99999,
					99999,
					!!articleData.chargedByWeight,
					articleData.weightPerPiece?.value
				)
			})
			.catch((error) => {
				console.error("addItem in CartRecommendationStore failed with ", error);
			});
	};
	onCartStoreUpdate = () => {
		const { cartId } = this._cartStore.getCart() || {};

		if (cartId && cartId !== this._state.cartId) {
			this._state = { ...this._state, cartId, prediction: null };
			this.trigger();
		}

		this.refreshRecommendations().catch(() => {});
	};

	async hideRecommendation(bundleId: string) {
		try {
			const { customerId, country, storeId } = this._cartStore.getCart() || {};
			if (!customerId || !country || !storeId) return;

			this._hiddenBundles.add(bundleId);

			await downvoteBundle(country, storeId, customerId, bundleId);
		} catch (e) {
			trackUiError("CartmancerRequest", "Could not put downvoted bundle id.", e as any);
			return;
		}

		this.trigger();
	}

	/* eslint-disable complexity */
	async refreshRecommendations() {
		const { items, customerId, country, storeId } = this._cartStore.getCart() || {};
		const locale = SessionStore.getLocale();
		if (!this.enabled || !customerId || !country || !storeId || !locale) return;

		try {
			const prediction = await getDidYouForgetRecommendation(
				customerId,
				Object.keys(items || {}),
				country,
				storeId,
				locale
			);

			this._cartItemStore.refreshWatchEntries(
				prediction.items.reduce((acc, it) => ({ ...acc, [it]: null }), {}),
				"CartRecommendationStore"
			);

			this._state = { ...this._state, prediction };
			this.trigger();
		} catch (e) {
			// This is a totally optional functionality, so we just track the error and
			// do nothing.
			trackUiError("CartRecommendationStore", "Did You Forget? Failed.", e as any);
		}
	}

	get enabled() {
		return (
			GdprStorage.localStorage.get("cart-recommendation-opt-out").actualResult !==
			CART_RECOMMENDATION_OPT_OUT_VALUE
		);
	}

	getState(): CartRecommendationStoreState {
		const { prediction } = this._state;
		if (!prediction)
			return {
				prediction: null,
				enabled: false,
				numberOfItemsInCart: null
			};
		const articleData = this._cartItemStore.articleData;
		return {
			prediction: {
				predictionId: prediction.predictionId,
				items: prediction.items
					.filter((id) => !this._hiddenBundles.has(id))
					.map((id) => [id, articleData.get(id)])
			},
			enabled: this.enabled,
			numberOfItemsInCart: getNumberOfItems(this._cartStore.getCart())
		};
	}
}
